import React, { Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowUpRight, FiKey } from "react-icons/fi";
import { PortalGate, portalApi, usePortal } from "./PortalLayout";

const Connect = React.lazy(() => import("./Connect"));
const JobsResults = React.lazy(() => import("./JobsResults"));
const PortalFiles = React.lazy(() => import("./PortalFiles"));
const UsageBilling = React.lazy(() => import("./UsageBilling"));
const ApiSettings = React.lazy(() => import("../Pages/ApiSettings"));

export function ConnectPage() {
  const { preview, token, basePath = "" } = usePortal();
  const navigate = useNavigate();
  const checkReadiness = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${portalApi}/user_api_key`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (!response.ok) throw new Error(response.status === 401 ? "Your session has expired. Return to simatomic.com to renew it." : "Unable to check your account. Please try again.");
      const data = await response.json();
      if (data.api_key_status === "active") {
        return { ready: true, message: "Your API key is active. Ask your assistant to check your SimAtomic account to verify its connection." };
      }
      return {
        ready: false,
        message: data.can_generate_api_key === true
          ? "Create an active API key, then add it to your assistant’s SimAtomic connection."
          : data.can_generate_api_key === false
          ? "Demo accounts cannot generate API keys. Contact your administrator for API access."
          : "API-key generation is unavailable. Contact your administrator for API access.",
      };
    } catch (error) {
      if (error.name === "AbortError") throw new Error("The API did not respond in time. Please try again.");
      if (error instanceof TypeError) throw new Error("Unable to reach the API. Check that your development server is running.");
      throw error;
    } finally { clearTimeout(timeout); }
  };
  return <Suspense fallback={<p>Loading Connect…</p>}><Connect helpPath={`${basePath}/help`} key={preview ? "preview" : "account"} preview={preview} onManageKeys={() => navigate(`${basePath}/connect/api`)} onCheckConnection={preview ? undefined : checkReadiness} /></Suspense>;
}

export function JobsPage() {
  const { project, preview, token, basePath } = usePortal();
  return <PortalGate needsProject><Suspense fallback={<p>Loading jobs…</p>}><JobsResults sessionToken={token} showDocking={!basePath} preview={preview} key={project ? `${project.workspaceId}/${project.projectId}` : "none"} workspaceId={project?.workspaceId} projectId={project?.projectId} /></Suspense></PortalGate>;
}

export function FilesPage() {
  const { project, preview } = usePortal();
  return <PortalGate needsProject><Suspense fallback={<p>Loading files…</p>}><PortalFiles preview={preview} key={project ? `${project.workspaceId}/${project.projectId}` : "none"} workspaceId={project?.workspaceId} projectId={project?.projectId} /></Suspense></PortalGate>;
}

export function UsagePage() {
  const { preview } = usePortal();
  return <PortalGate><Suspense fallback={<p>Loading usage…</p>}><UsageBilling preview={preview} /></Suspense></PortalGate>;
}

export function KeysPage() {
  const { preview, basePath = "" } = usePortal();
  if (preview) return <><div className="sa-portal-page-heading"><h1>API key</h1><p>API key management is available with your workspace session.</p></div><Link to={`${basePath}/connect`}>Back to Connect</Link></>;
  return <PortalGate><Link className="sa-shell-back" to={`${basePath}/connect`}>← Back to Connect</Link><Suspense fallback={<p>Loading API settings…</p>}><ApiSettings /></Suspense></PortalGate>;
}

export function AccountPage() {
  const { account, role, preview, basePath = "" } = usePortal();
  return <PortalGate><div className="sa-portal-page-heading"><h1>Account</h1><p>Your profile and access to SimAtomic.</p></div><section className="sa-portal-account-card"><h2>Account details</h2><dl><dt>Name</dt><dd>{account.username || "—"}</dd><dt>Email</dt><dd>{account.email || "—"}</dd><dt>Role</dt><dd>{role || "—"}</dd></dl><Link className="sa-shell-button" to={`${basePath}/connect/api`}><FiKey />Manage API key</Link></section>{!preview && <section className="sa-portal-help-card"><h2>Workspace access</h2><p>Your default workspace and project are set up automatically at Google sign-in. Your assistant’s submissions use this project unless you select another.</p>{role === "admin" && <p><Link to="/admin">Administration <FiArrowUpRight /></Link></p>}</section>}</PortalGate>;
}

export function HelpPage() {
  const { basePath = "" } = usePortal();
  return <><div className="sa-portal-page-heading"><h1>Help</h1><p>Connections, jobs, and workspace support.</p></div><div className="sa-portal-help-topics">
    <details><summary>Connect an assistant</summary><p>Choose your assistant on Connect and follow the setup steps. An active API key means your account is ready. Ask your assistant to check your SimAtomic account to confirm the connection.</p><Link to={`${basePath}/connect`}>Open Connect <FiArrowUpRight /></Link></details>
    <details><summary>Prepare a simulation</summary><p>Ask your assistant to validate the structure and explain the protocol, duration, replicas, timestep, output interval, and estimated credits. Review those details before submitting a paid job.</p></details>
    <details><summary>Find results</summary><p>Choose a project in Jobs & Results to see its simulation, analysis, and MM/PBSA records. Open a report or download outputs from a completed job. Docking results open in the existing viewer.</p><Link to={`${basePath}/jobs`}>Open Jobs & Results <FiArrowUpRight /></Link></details>
    <details><summary>Troubleshoot a failed job</summary><p>Keep the job ID and error message. Ask your assistant whether the input needs correction before retrying, or send those details to support.</p><a href="mailto:info@simatomic.com">info@simatomic.com <FiArrowUpRight /></a></details>
  </div></>;
}
